import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { EditorContent } from '@tiptap/react';
import { memo, useEffect, useRef } from 'react';
import { useEditor as useInfographEditor } from 'widgets/sdk';

import { findFirstFontFamily } from '../common/TextBasedWidgets.helpers';
import { WidgetState } from '../common/TextBasedWidgets.types';
import { isPositionWithinCurrentSelection } from './TableWidget.helpers';
import { EditorStateWithTableColumn, TableEditorProps } from './TableWidget.types';

export const TableEditor = memo(({ widgetState, editor, onBlur, caretColor, onResize }: TableEditorProps) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const { boundingBox, hideContextMenu } = useInfographEditor();

  const isEditable = widgetState === WidgetState.Edit;
  const isActive = widgetState === WidgetState.Active;
  const state = editor?.view.state as EditorStateWithTableColumn;
  const isResizing: boolean = !!state?.columnResizing$?.dragging;

  useEffect(() => {
    if (!tableWrapperRef.current) return;

    const table = tableWrapperRef.current.querySelector('table');
    if (!table) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.target.clientHeight;
        const width = entry.target.clientWidth;
        // getting the widget element
        const widgetElement = entry.target.closest('.widget-container') as HTMLElement;
        if ((isEditable || isActive) && typeof height === 'number' && height > 0) {
          onResize?.({ width, height, isResizing: true });
          boundingBox.updateRect();
          if (widgetElement) {
            widgetElement.style.height = `${height}px`;
            widgetElement.style.width = `${width}px`;
          }
        }
      }
    });

    observer.observe(table);

    return () => {
      observer.disconnect();
    };
  }, [editor, onResize, isEditable, isActive, boundingBox, isResizing]);

  if (!editor) return <></>;

  const stopPropagation = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (isEditable) e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    hideContextMenu();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Allow context menu handler in wrapper component to trigger
    // when right-click on cell
    if (isEditable && e.button === 2) {
      // If right-click event occurs outside of the current selection, should update the selection state
      // Otherwise prevent updating selection state
      if (!isPositionWithinCurrentSelection(editor, { top: e.clientY, left: e.clientX })) return;
      e.preventDefault();
    } else {
      hideContextMenu();
      stopPropagation(e);
    }
  };

  const fontFamily = findFirstFontFamily(editor);
  return (
    <StyledEditor
      _hover={{ cursor: isEditable ? 'text' : 'default' }}
      h={isResizing ? 'fit-content' : '100%'}
      cursor={isEditable ? '' : 'move !important'}
      position='relative'
      onClick={stopPropagation}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      ref={tableWrapperRef}
      pointerEvents={widgetState !== WidgetState.Default ? 'auto' : 'none'}
      // The input cursor color set to the same color as the text (applies for blank lines without text)
      color={caretColor}
      // for line-height consistency when exporting to pdf - the parent div should have the same font family as the editor
      // obs: there are probably edge-cases here with multiple fonts in the same text widget
      fontFamily={fontFamily}
    >
      <EditorContent editor={editor} />
    </StyledEditor>
  );
});
TableEditor.displayName = 'TableEditor';

export const StyledEditor = styled(Box)`
  .ProseMirror,
  .ProseMirror .tableWrapper {
    position: fixed;
    width: 100%;
    height: ${(props) => props?.h};
    min-height: fit-content;
    overflow: hidden;
    outline: none;
  }
  .ProseMirror table {
    height: ${(props) => props?.h};
    min-height: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    width: 100% !important;
    overflow: hidden;
  }
  .ProseMirror th,
  .ProseMirror td {
    min-width: 1em;
    width: 1px;
    overflow-wrap: break-word;
    white-space: pre-wrap;
    background-clip: padding-box;
    overflow: hidden;
  }
`;
